import { Button, type ButtonProps, Space, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type AppDispatch, type RootState } from '../reducers';
import { nextLanguage } from '../reducers/language/reducer';
import { SPACING } from '../theme/tokens';

const LANGUAGE_CODE_EN = 'EN';

export interface ITextTranslate {
  text: string;
  callbacks: ((translatedText: string, languageCode: string) => void)[];
}

const insertTranslation = async (
  textTranslate: ITextTranslate,
  languageCode: string,
): Promise<void> => {
  const text: string = await navigator.clipboard.readText();
  const tt: string = text.trim();

  textTranslate.callbacks.forEach((callback) => callback(tt, languageCode));

  await navigator.clipboard.writeText('');
};

const startAutomat = async (
  texts: ITextTranslate[],
  fromLanguageCode: string,
  toLanguageCode: string,
  setRunning: React.Dispatch<React.SetStateAction<boolean>>,
  submit: () => void,
  dispatch: AppDispatch,
): Promise<void> => {
  setRunning(true);

  for (const textTranslate of texts) {
    await new Promise<void>((resolve) => {
      window.addEventListener(
        'focus',
        async (): Promise<void> => {
          await insertTranslation(textTranslate, toLanguageCode);

          setTimeout((): void => {
            submit();
          }, 300);

          setTimeout((): void => {
            dispatch(nextLanguage());
            setRunning(false);
            resolve();
          }, 500);
        },
        { once: true },
      );

      const gtLink = new URL('https://translate.google.com/');
      gtLink.searchParams.append('sl', fromLanguageCode.toLowerCase());
      gtLink.searchParams.append('tl', toLanguageCode.toLowerCase());
      gtLink.searchParams.append('text', textTranslate.text.trim());
      gtLink.searchParams.append('op', 'translate');

      window.open(gtLink.toString(), '_blank');
    });
  }
};

function GoogleTranslateAutomat({
  texts,
  submit,
  isSaving,
  size = 'middle',
}: {
  texts: ITextTranslate[];
  submit: () => void;
  isSaving: boolean;
  size?: ButtonProps['size'];
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [isRunning, setRunning] = useState(false);
  const [runAgain, setRunAgain] = useState(false);
  const [actualLanguageCode, setActualLanguageCode] = useState<string | null>();

  useEffect((): void => {
    if (
      !languageType ||
      !runAgain ||
      isRunning ||
      isSaving ||
      texts.length === 0 ||
      actualLanguageCode === languageType?.code
    ) {
      return;
    }

    if (languageType?.code === LANGUAGE_CODE_EN) {
      setRunAgain(false);
      return;
    }

    setActualLanguageCode(languageType?.code);

    startAutomat(
      texts,
      LANGUAGE_CODE_EN,
      languageType.code,
      setRunning,
      submit,
      dispatch,
    );
  }, [
    runAgain,
    isRunning,
    isSaving,
    languageType?.code,
    texts,
    submit,
    dispatch,
    actualLanguageCode,
  ]);

  if (
    isSaving ||
    texts.length === 0 ||
    LANGUAGE_CODE_EN === languageType?.code
  ) {
    return (
      <Space size={SPACING.lg} align="start">
        <Button type="primary" size={size} disabled>
          GT Automat
        </Button>
      </Space>
    );
  }

  return (
    <Space size={SPACING.lg} align="start">
      <Button
        type="primary"
        size={size}
        disabled={isRunning}
        onClick={(): void => {
          if (!languageType) {
            return;
          }

          startAutomat(
            texts,
            LANGUAGE_CODE_EN,
            languageType.code,
            setRunning,
            submit,
            dispatch,
          );

          setRunAgain(true);
        }}
      >
        GT Automat
      </Button>
      <Spin size="small" spinning={isRunning} />
    </Space>
  );
}

export default GoogleTranslateAutomat;
